import type { Feature } from "geojson";

import { hasCoordinates, isSameCoord, validateGeometry } from "./geometryUtils";

/**
 * Remove a coordinate from a feature at the specified nested index path
 */
export default function removeNestedCoordinate(
  feature: Feature,
  nestedIdx: string,
): Feature {
  if (!hasCoordinates(feature.geometry)) {
    throw new Error("Feature geometry is null or does not have coordinates");
  }

  const indices = nestedIdx.split(".").map(Number);

  function removeCoordinate(coords: any, indices: number[]): any {
    if (indices.length === 1) {
      const idxToRemove = indices[0];
      const newCoords = coords.filter((_: any, i: number) => i !== idxToRemove);

      // Handle polygon closure
      if (
        feature.geometry.type === "Polygon" ||
        feature.geometry.type === "MultiPolygon"
      ) {
        const isClosed =
          coords.length > 3 &&
          isSameCoord(coords[0], coords[coords.length - 1]);
        if (
          isClosed &&
          !isSameCoord(newCoords[0], newCoords[newCoords.length - 1])
        ) {
          newCoords.push([...newCoords[0]]);
        }
      }

      return newCoords;
    }

    return coords.map((coord: any, i: number) =>
      i === indices[0] ? removeCoordinate(coord, indices.slice(1)) : coord,
    );
  }

  const updatedCoordinates = removeCoordinate(
    feature.geometry.coordinates,
    indices,
  );

  const updatedFeature = {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: updatedCoordinates,
    },
  };

  // Validate the result after modification
  validateGeometry(updatedFeature);

  return updatedFeature;
}
